import { compact, filter, groupBy, keyBy, map, orderBy } from "lodash-es";

import {
  AssistantProjectStatus,
  type AssistantFollowUpObservable,
  type AssistantProjectObservable,
  type AssistantTaskObservable,
} from "@/store/assistant";
import { formatAgentDate, getAgentTimestamp } from "@/pages/agent/utils/agent-date.utils";
import { isClosedFollowUp } from "@/pages/agent/utils/agent-follow-up.utils";
import { buildAgentSearchableText } from "@/pages/agent/utils/agent-search.utils";
import { normalizeStatusValue } from "@/pages/agent/utils/agent-status.utils";
import { isClosedTaskStatus } from "@/pages/agent/tasks/agent-tasks-page.utils";
import { ProjectSortKey } from "@/pages/agent/projects/use-agent-projects-search-params";

export enum ProjectSectionKind {
  Active = "active",
  Completed = "completed",
}

export type ProjectCardViewModel = {
  project: AssistantProjectObservable;
  openTasksCount: number;
  nextUp: ProjectNextUp | null;
  isClosed: boolean;
  searchableText: string;
};

export type ProjectNextUp = {
  label: string;
  detail: string;
};

type FollowUpProjectGrouping = {
  projectId: string;
  followUp: AssistantFollowUpObservable;
};

export const DEFAULT_PROJECT_STATUS_FILTER_VALUES = [
  AssistantProjectStatus.Active,
  AssistantProjectStatus.Resolved,
  AssistantProjectStatus.Archived,
];

const CLOSED_PROJECT_STATUS_VALUES = new Set<string>([
  AssistantProjectStatus.Resolved,
  AssistantProjectStatus.Archived,
]);

export const isClosedProjectStatus = ({ status }: { status: string }) =>
  CLOSED_PROJECT_STATUS_VALUES.has(normalizeStatusValue({ status }));

/**
 * Builds project cards from the project, task, and follow-up sync streams.
 *
 * Follow-ups can point to a project directly or through a task. Resolving both
 * shapes here keeps the rendering layer from needing to know backend linkage details.
 */
export const buildProjectCards = ({
  projects,
  tasks,
  followUps,
}: {
  projects: AssistantProjectObservable[];
  tasks: AssistantTaskObservable[];
  followUps: AssistantFollowUpObservable[];
}) => {
  const tasksWithProject = filter(tasks, task => Boolean(task.projectId));
  const tasksById = keyBy(tasksWithProject, task => task.id);
  const tasksByProjectId = groupBy(tasksWithProject, task => task.projectId ?? "");
  const followUpsByProjectId = groupBy(
    compact(
      map(followUps, followUp => {
        const projectId = getFollowUpProjectId({ followUp, tasksById });

        if (!projectId) {
          return null;
        }

        return {
          projectId,
          followUp,
        } satisfies FollowUpProjectGrouping;
      })
    ),
    followUpProjectGrouping => followUpProjectGrouping.projectId
  );

  return map(projects, project => {
    const projectTasks = tasksByProjectId[project.id] ?? [];
    const openTasks = filter(projectTasks, task => !isClosedTaskStatus({ status: task.status }));
    const projectFollowUps = map(
      followUpsByProjectId[project.id] ?? [],
      followUpProjectGrouping => followUpProjectGrouping.followUp
    );

    return {
      project,
      openTasksCount: openTasks.length,
      nextUp: getProjectNextUp({ openTasks, followUps: projectFollowUps }),
      isClosed: isClosedProjectStatus({ status: project.status }),
      searchableText: buildAgentSearchableText({
        values: [project.title, project.description, project.status],
      }),
    } satisfies ProjectCardViewModel;
  });
};

const getFollowUpProjectId = ({
  followUp,
  tasksById,
}: {
  followUp: AssistantFollowUpObservable;
  tasksById: Record<string, AssistantTaskObservable | undefined>;
}) => {
  if (followUp.projectId) {
    return followUp.projectId;
  }

  if (!followUp.taskId) {
    return null;
  }

  return tasksById[followUp.taskId]?.projectId ?? null;
};

const getProjectNextUp = ({
  openTasks,
  followUps,
}: {
  openTasks: AssistantTaskObservable[];
  followUps: AssistantFollowUpObservable[];
}): ProjectNextUp | null => {
  const openFollowUps = filter(followUps, followUp => !isClosedFollowUp({ followUp }));
  const nextFollowUp = orderBy(
    openFollowUps,
    followUp =>
      getAgentTimestamp({
        value: followUp.scheduledFor ?? followUp.dueAt,
        fallback: Number.POSITIVE_INFINITY,
      }),
    "asc"
  )[0];

  if (nextFollowUp) {
    return {
      label: nextFollowUp.content,
      detail: formatAgentDate({
        value: nextFollowUp.scheduledFor ?? nextFollowUp.dueAt,
        includeTime: true,
      }),
    };
  }

  const nextTask = orderBy(
    openTasks,
    [
      task =>
        getAgentTimestamp({
          value: task.dueAt,
          fallback: Number.POSITIVE_INFINITY,
        }),
      task =>
        getAgentTimestamp({
          value: task.updatedAt,
          fallback: 0,
        }),
    ],
    ["asc", "desc"]
  )[0];

  if (!nextTask) {
    return null;
  }

  return {
    label: nextTask.title || "Untitled task",
    detail: formatAgentDate({ value: nextTask.dueAt, includeTime: true }),
  };
};

export const getSortedProjectCards = ({
  projectCards,
  sortKey,
}: {
  projectCards: ProjectCardViewModel[];
  sortKey: ProjectSortKey;
}) => {
  if (sortKey === ProjectSortKey.Alphabetical) {
    return orderBy(projectCards, projectCard => projectCard.project.title.toLowerCase(), "asc");
  }

  return orderBy(
    projectCards,
    projectCard =>
      getAgentTimestamp({
        value: projectCard.project.lastActivityAt ?? projectCard.project.updatedAt,
        fallback: 0,
      }),
    "desc"
  );
};
